<script>
/**
 * Renders one of 13 thin-ink botanical SVG icons using currentColor.
 * Color is controlled by the parent element's CSS `color` property.
 *
 * @param {string}  name    - sprig | leaf | sun | cloud | berry | flower |
 *                            fern | mountain | waves | wheat | moon | bird | tree
 * @param {number}  size    - Width in px (height derived from aspect ratio). Default 36.
 * @param {number}  rotate  - Rotation in degrees. Default 0.
 * @param {number}  opacity - Opacity 0–1. Default 1.
 * @returns SVG element
 */

import { h } from 'vue'

// Each icon: [tag, props] pairs for children
const ICONS = {
  sprig: {
    viewBox: '0 0 36 36', aspect: 1, sw: 0.9,
    children: [
      ['path', { d: 'M18 32 C18 22, 18 12, 18 6' }],
      ['path', { d: 'M18 24 C13 23, 9 20, 8 17 C12 17, 16 19, 18 22' }],
      ['path', { d: 'M18 18 C23 17, 27 14, 28 11 C24 11, 20 13, 18 16' }],
      ['path', { d: 'M18 12 C14 11, 11 9, 10 7 C13 7, 16 8, 18 10' }],
      ['path', { d: 'M18 6 C18.6 5.4, 19.6 5, 20.6 5' }],
    ],
  },
  leaf: {
    viewBox: '0 0 28 28', aspect: 1, sw: 0.9,
    children: [
      ['path', { d: 'M5 22 C8 12, 16 5, 24 5 C24 13, 17 21, 7 23 Z' }],
      ['path', { d: 'M7 22 C12 18, 18 12, 23 7' }],
      ['path', { d: 'M11 19 L13.5 17.5 M14 16 L16.5 14.5 M17 13 L19.5 11.5' }],
    ],
  },
  sun: {
    viewBox: '0 0 40 40', aspect: 1, sw: 0.9,
    children: [
      ['circle', { cx: '20', cy: '20', r: '6' }],
      ['path', { d: 'M20 4 V10 M20 30 V36 M4 20 H10 M30 20 H36' }],
      ['path', { d: 'M8.5 8.5 L12.5 12.5 M27.5 27.5 L31.5 31.5 M8.5 31.5 L12.5 27.5 M27.5 12.5 L31.5 8.5' }],
    ],
  },
  cloud: {
    viewBox: '0 0 60 33', aspect: 0.55, sw: 0.9,
    children: [
      ['path', { d: 'M10 24 C5 24, 4 18, 9 17 C9 12, 16 11, 18 14 C19 9, 28 9, 30 14 C33 11, 40 13, 40 18 C46 17, 48 24, 43 25 L11 25 Z' }],
    ],
  },
  berry: {
    viewBox: '0 0 32 32', aspect: 1, sw: 0.9,
    children: [
      ['path', { d: 'M16 30 C16 22, 16 14, 16 6' }],
      ['path', { d: 'M16 18 C12 17, 9 14, 9 11' }],
      ['path', { d: 'M16 14 C20 13, 23 10, 23 7' }],
      ['circle', { cx: '9', cy: '11', r: '1.6' }],
      ['circle', { cx: '23', cy: '7', r: '1.6' }],
      ['circle', { cx: '16', cy: '6', r: '1.6' }],
    ],
  },
  flower: {
    viewBox: '0 0 36 36', aspect: 1, sw: 0.9,
    children: [
      ['circle', { cx: '18', cy: '14', r: '2.5' }],
      ['ellipse', { cx: '18', cy: '7', rx: '2.6', ry: '4.5' }],
      ['ellipse', { cx: '25', cy: '14', rx: '4.5', ry: '2.6' }],
      ['ellipse', { cx: '11', cy: '14', rx: '4.5', ry: '2.6' }],
      ['ellipse', { cx: '22', cy: '9', rx: '2.6', ry: '4', transform: 'rotate(45 22 9)' }],
      ['ellipse', { cx: '14', cy: '9', rx: '2.6', ry: '4', transform: 'rotate(-45 14 9)' }],
      ['path', { d: 'M18 17 C18 22, 17 28, 14 33' }],
      ['path', { d: 'M16 24 C14 23, 12 22, 11 20' }],
    ],
  },
  fern: {
    viewBox: '0 0 44 44', aspect: 1, sw: 0.85,
    children: [
      ['path', { d: 'M8 40 C14 32, 22 22, 36 8' }],
      ['path', { d: 'M14 32 C12 28, 11 25, 11 22' }],
      ['path', { d: 'M19 26 C18 22, 18 19, 19 16' }],
      ['path', { d: 'M24 21 C24 17, 25 14, 27 11' }],
      ['path', { d: 'M28 17 C29 14, 31 12, 33 10' }],
      ['path', { d: 'M14 32 C18 32, 21 30, 22 28' }],
      ['path', { d: 'M19 26 C23 27, 26 26, 28 23' }],
      ['path', { d: 'M24 21 C28 22, 31 21, 33 18' }],
    ],
  },
  mountain: {
    viewBox: '0 0 60 30', aspect: 0.5, sw: 0.9,
    children: [
      ['path', { d: 'M2 26 L14 10 L22 18 L32 6 L44 20 L52 14 L58 26' }],
      ['path', { d: 'M14 10 L17 13 M32 6 L36 10 M44 20 L41 17' }],
    ],
  },
  waves: {
    viewBox: '0 0 80 16', aspect: 0.2, sw: 0.85,
    children: [
      ['path', { d: 'M2 6 Q10 2, 18 6 T34 6 T50 6 T66 6 T78 6' }],
      ['path', { d: 'M2 12 Q10 8, 18 12 T34 12 T50 12 T66 12 T78 12', opacity: '0.5' }],
    ],
  },
  wheat: {
    viewBox: '0 0 32 32', aspect: 1, sw: 0.9,
    children: [
      ['path', { d: 'M16 30 V8' }],
      ['path', { d: 'M16 8 L13 4 M16 8 L19 4 M16 8 L16 3' }],
      ['path', { d: 'M16 14 L12 11 M16 14 L20 11' }],
      ['path', { d: 'M16 18 L12 15 M16 18 L20 15' }],
      ['path', { d: 'M16 22 L12 19 M16 22 L20 19' }],
    ],
  },
  moon: {
    viewBox: '0 0 32 32', aspect: 1, sw: 0.9,
    children: [
      ['path', { d: 'M22 6 C16 8, 12 13, 12 19 C12 24, 16 28, 22 28 C16 28, 10 22, 10 16 C10 11, 14 7, 22 6 Z' }],
    ],
  },
  bird: {
    viewBox: '0 0 24 12', aspect: 0.5, sw: 0.9,
    children: [
      ['path', { d: 'M2 8 Q6 2, 12 6 Q18 2, 22 8' }],
    ],
  },
  tree: {
    viewBox: '0 0 40 40', aspect: 1, sw: 0.9,
    children: [
      ['path', { d: 'M20 38 V22' }],
      ['path', { d: 'M20 22 C12 22, 8 17, 9 12 C5 10, 6 5, 11 5 C12 1, 18 1, 20 4 C22 1, 28 1, 29 5 C34 5, 35 10, 31 12 C32 17, 28 22, 20 22 Z' }],
      ['path', { d: 'M20 28 L16 24 M20 32 L24 28' }],
    ],
  },
}

export default {
  name: 'BotanicalIcon',

  props: {
    name:    { type: String, required: true },
    size:    { type: Number, default: 36 },
    rotate:  { type: Number, default: 0 },
    opacity: { type: Number, default: 1 },
  },

  render() {
    const ico = ICONS[this.name] ?? ICONS.sprig
    const style = {}
    if (this.rotate)        style.transform = `rotate(${this.rotate}deg)`
    if (this.opacity !== 1) style.opacity   = this.opacity

    return h('svg', {
      width:              this.size,
      height:             Math.round(this.size * ico.aspect),
      viewBox:            ico.viewBox,
      fill:               'none',
      stroke:             'currentColor',
      'stroke-width':     String(ico.sw),
      'stroke-linecap':   'round',
      'stroke-linejoin':  'round',
      style,
    }, ico.children.map(([tag, props]) => h(tag, props)))
  },
}
</script>
